import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { AuthShell } from '../components/AuthShell.tsx';
import { ErrorNotice, Field, Spinner } from '../components/ui.tsx';
import { ApiRequestError, api } from '../lib/api.ts';

/**
 * Serves both halves of the reset flow: without a `uuid` it asks for an email,
 * with one it sets the new password.
 */
export function PasswordResetPage(): React.JSX.Element {
  const { uuid } = useParams<{ uuid?: string }>();
  const navigate = useNavigate();

  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    setSubmitting(true);

    try {
      if (uuid) {
        await api.post('/auth/password-reset/confirm', { uuid, password: value });
        navigate('/login', { replace: true });
      } else {
        await api.post('/auth/password-reset/request', { email: value });
        setSent(true);
      }
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        setError(caught.message);
        setFieldErrors(caught.fieldErrors);
      } else {
        setError('Something went wrong');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <AuthShell
        title="Check your email"
        subtitle="If that address has an account, a reset link is on its way. The link expires in an hour."
        footer={
          <Link to="/login" className="font-medium text-brand-600 hover:underline dark:text-brand-400">
            Back to sign in
          </Link>
        }
      >
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Didn&apos;t get it? Check your spam folder, then try again.
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={uuid ? 'Choose a new password' : 'Reset your password'}
      subtitle={
        uuid
          ? 'Pick something you have not used before.'
          : "Enter your email and we'll send you a reset link."
      }
      footer={
        <Link to="/login" className="font-medium text-brand-600 hover:underline dark:text-brand-400">
          Back to sign in
        </Link>
      }
    >
      <form className="space-y-4" onSubmit={(event) => void handleSubmit(event)} noValidate>
        {error ? <ErrorNotice message={error} /> : null}

        {uuid ? (
          <Field
            label="New password"
            htmlFor="password"
            error={fieldErrors.password}
            hint="At least 8 characters."
          >
            <input
              id="password"
              type="password"
              className={clsx('lg-input', fieldErrors.password && 'lg-input-error')}
              autoComplete="new-password"
              autoFocus
              required
              minLength={8}
              value={value}
              onChange={(event) => {
                setValue(event.target.value);
              }}
            />
          </Field>
        ) : (
          <Field label="Email" htmlFor="email" error={fieldErrors.email}>
            <input
              id="email"
              type="email"
              className={clsx('lg-input', fieldErrors.email && 'lg-input-error')}
              autoComplete="username"
              autoFocus
              required
              value={value}
              onChange={(event) => {
                setValue(event.target.value);
              }}
            />
          </Field>
        )}

        <button type="submit" className="lg-btn-primary w-full" disabled={submitting}>
          {submitting ? <Spinner /> : null}
          {uuid ? 'Save new password' : 'Send reset link'}
        </button>
      </form>
    </AuthShell>
  );
}
